import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { ArrowNarrowLeftIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { HorizontalTabs, TabItem } from 'common/horizontal-tabs';
import { AppContext } from 'context';
import { vaultAccess } from 'helpers/sharing';
import { getLastVaultId, goToDashboard } from 'helpers/vault-breadcrumbs';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { goToPage, RouteName } from 'routes';
import styles from './settings-layout.module.scss';

export type SettingsTab = 'account' | 'vault';

type SettingsLayoutProps = {
  activeTab: SettingsTab;
  // The left-side navigation: the account, or the vaults to pick from.
  nav: ReactNode;
  children: ReactNode;
};

const NO_VAULT_SETTINGS =
  "Only a vault's owner and editors can change its settings.";

// Frame shared by Account Settings and Vault Settings: back link, title, the
// tabs that switch between the two, a left-side navigation and the content.
const SettingsLayout = ({ activeTab, nav, children }: SettingsLayoutProps) => {
  const {
    state: {
      currentVault,
      data: { user },
    },
  } = useContext(AppContext);
  const [switching, setSwitching] = useState(false);
  // The vault the Vault Settings tab opens, looked up on Account Settings:
  // undefined while that runs, null when there's none (the tab is off).
  const [vaultTarget, setVaultTarget] = useState<number | null>();
  const vaultTargetRequest = useRef<Promise<number | null> | null>(null);

  // The vault the user was in (or opened last), when they may manage it: a
  // viewer or contributor there gets no settings. Without one, the first
  // vault they own or edit.
  const resolveVaultId = async (): Promise<number | null> => {
    const contextId =
      (currentVault?.id ?? 0) > 0 ? currentVault!.id : getLastVaultId();
    if (contextId) {
      try {
        const vault =
          currentVault?.id === contextId && currentVault.myAccess
            ? currentVault
            : await VaultsController.getVault(contextId, 'summary');
        return vaultAccess(user, vault).canManageVault ? contextId : null;
      } catch {
        // Gone, or no longer theirs: as if they hadn't been in one.
      }
    }
    const [first] = await VaultsController.getManagedVaults(1);
    return first?.id ?? null;
  };

  useEffect(() => {
    if (activeTab !== 'account') return undefined;
    let live = true;
    const request = resolveVaultId().catch(() => null);
    vaultTargetRequest.current = request;
    request.then((id) => {
      if (live) setVaultTarget(id);
    });
    return () => {
      live = false;
    };
  }, [activeTab]);

  const tabs: TabItem<SettingsTab>[] = [
    { id: 'account', label: 'Account Settings' },
    {
      id: 'vault',
      label: 'Vault Settings',
      disabled: activeTab === 'account' && vaultTarget === null,
      title: vaultTarget === null ? NO_VAULT_SETTINGS : undefined,
    },
  ];

  const onTabChange = async (tab: SettingsTab) => {
    if (tab === activeTab || switching) return;
    if (tab === 'account') {
      goToPage(RouteName.Account);
      return;
    }
    setSwitching(true);
    try {
      const vaultId =
        vaultTarget !== undefined
          ? vaultTarget
          : await vaultTargetRequest.current;
      if (vaultId) goToPage(RouteName.VaultSettings, { id: vaultId });
    } finally {
      setSwitching(false);
    }
  };

  const onBack = () => {
    const vaultId =
      (currentVault?.id ?? 0) > 0 ? currentVault!.id : getLastVaultId();
    if (vaultId) goToDashboard({ vaultId });
    else goToPage(RouteName.Vaults);
  };

  return (
    <div className={styles.settingsLayout}>
      <div className={styles.header}>
        <Button
          buttonStyle={ButtonStyle.LinkGray}
          buttonSize={ButtonSize.Small}
          className={styles.backButton}
          onClick={onBack}
        >
          <ArrowNarrowLeftIcon />
          Back
        </Button>
        <div className={styles.titleAndTabs}>
          <h1 className="text__heading4__gray900">Settings</h1>
          <HorizontalTabs
            size="medium"
            tabs={tabs}
            activeTab={activeTab}
            onChange={onTabChange}
          />
        </div>
      </div>
      <div className={styles.body}>
        <nav className={styles.nav}>{nav}</nav>
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};

export { SettingsLayout };
